import React, { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { MessageSquare } from 'lucide-react';
import { TbClockHour10 } from 'react-icons/tb';
import useScrollLock from '../../../hooks/useScrollLock';
import TimeKeeper from 'react-timekeeper';
import { TeamCheckin } from '../../../store/slices/teamFocusSession/types';

interface CheckInModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (title: string, checkinTime: string) => void;
    onUpdate?: (id: string, title: string, checkinTime: string) => void;
    initialCheckin?: TeamCheckin | null;
}

// Extract HH:mm from an ISO 8601 string for the time picker
const isoToHHmm = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso; // already HH:mm or unknown
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Convert HH:mm to a full ISO 8601 string (today's date + given time)
const hhmmToISO = (hhmm: string): string => {
    const [hours, minutes] = hhmm.split(':').map(Number);
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d.toISOString();
};

const CheckInModal: React.FC<CheckInModalProps> = ({ isOpen, onClose, onAdd, onUpdate, initialCheckin }) => {
    const [title, setTitle] = useState('');
    const [checkinTime, setCheckinTime] = useState(''); // always stored as HH:mm internally
    const [showTimePicker, setShowTimePicker] = useState(false);

    const isEditing = !!initialCheckin;

    useEffect(() => {
        if (isOpen) {
            if (initialCheckin) {
                setTitle(initialCheckin.title);
                setCheckinTime(isoToHHmm(initialCheckin.checkinTime));
            } else {
                setTitle('');
                setCheckinTime('');
            }
        }
    }, [isOpen, initialCheckin]);

    useScrollLock(isOpen || showTimePicker);

    const handleSubmit = () => {
        if (title.trim() && checkinTime) {
            const isoTime = hhmmToISO(checkinTime);
            if (isEditing && onUpdate) {
                onUpdate(initialCheckin!.id, title.trim(), isoTime);
            } else {
                onAdd(title.trim(), isoTime);
            }
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-xl">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <MessageSquare className="w-6 h-6 text-orange-600" />
                        <h3 className="text-2xl font-medium text-gray-900">
                            {isEditing ? 'Edit Check-in' : 'New Check-in'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="What are you checking in about?"
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Time
                        </label>
                        <button
                            onClick={() => setShowTimePicker(true)}
                            className="w-full flex items-center justify-between bg-white text-gray-900 rounded-xl p-3 border border-gray-200/60 focus:border-orange-300 focus:outline-none text-sm h-[46px]"
                        >
                            <span className={!checkinTime ? 'text-gray-400' : ''}>
                                {checkinTime ? new Date(`2000-01-01T${checkinTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'Select time'}
                            </span>
                            <TbClockHour10 className="text-lg" color="rgba(255, 89, 36, 0.9)" />
                        </button>

                        {showTimePicker && (
                            <div
                                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
                                onClick={() => setShowTimePicker(false)}
                            >
                                <div
                                    className="shadow-2xl rounded-2xl overflow-hidden bg-white animate-in fade-in zoom-in duration-200"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <TimeKeeper
                                        time={checkinTime || '12:00'}
                                        onChange={(data) => setCheckinTime(data.formatted24)}
                                        onDoneClick={() => setShowTimePicker(false)}
                                        switchToMinuteOnHourSelect
                                        closeOnMinuteSelect={false}
                                        hour24Mode={false}
                                        coarseMinutes={1}
                                        forceCoarseMinutes
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!title.trim() || !checkinTime}
                            className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm ${!title.trim() || !checkinTime
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-700 text-white'
                                }`}
                        >
                            {isEditing ? 'Save Changes' : 'Add Check-in'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckInModal;
